import { Link } from 'react-router-dom';
import { 
  ArrowUpRight, Code, Cloud, Rocket, Users, Target, TrendingUp, 
  CheckCircle, Award, Briefcase, GraduationCap, Terminal
} from 'lucide-react';
import NewsletterForm from '@/components/NewsletterForm';

const Home = () => {
  const stats = [
    { value: '20', label: 'Students Per Cohort', icon: Users },
    { value: '8', label: 'Weeks to Launch', icon: Rocket },
    { value: '80%', label: 'Completion Rate', icon: Target },
    { value: '100%', label: 'Deployed Projects', icon: CheckCircle },
  ];

  const journey = [
    { 
      stage: 'Beginner',
      title: 'Learn the Basics',
      description: 'Start with zero experience and learn HTML, CSS, JavaScript fundamentals.',
      icon: Code,
      color: '#00AEEF'
    },
    { 
      stage: 'Builder',
      title: 'Build Real Projects',
      description: 'Create actual web applications with professional tools and workflows.',
      icon: Terminal,
      color: '#00FFE0'
    },
    { 
      stage: 'Presenter',
      title: 'Demo Day',
      description: 'Present your deployed project to industry professionals and community.',
      icon: Award,
      color: '#FF6A00'
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center overflow-hidden"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/a85da439-5cf8-4156-bae4-f830d8ae589c/images/c8e509ab47019c997e5b7bb296051f79b41aa00e88f1bcddf8c218aad4d9757d.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#060F22] via-[#060F22]/90 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="max-w-3xl">
            <span className="inline-block text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-6">
              Workforce Development Nonprofit
            </span>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-white leading-tight mb-6">
              Building the Next Generation of{' '}
              <span className="gradient-text">Tech Talent</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-[#94A3B8] leading-relaxed mb-8 max-w-2xl">
              From zero experience to deployed applications in 8 weeks. We don't teach code—we build futures through hands-on workforce development.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/enrollment"
                data-testid="hero-enroll-btn"
                className="inline-flex items-center justify-center bg-[#FF6A00] text-white font-bold rounded-full px-8 py-4 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] hover:-translate-y-1 transition-all duration-300 gap-2 text-lg"
              >
                Enroll Now
                <ArrowUpRight className="w-5 h-5" />
              </Link>
              <Link
                to="/partners"
                data-testid="hero-partner-btn"
                className="inline-flex items-center justify-center bg-transparent border-2 border-[#00AEEF] text-[#00AEEF] hover:bg-[#00AEEF]/10 hover:text-white font-bold rounded-full px-8 py-4 transition-all duration-300 gap-2 text-lg"
              >
                Partner With Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 sm:py-32 bg-[#060F22]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
                The Challenge
              </span>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
                Bridging the Tech Opportunity Gap
              </h2>
              <p className="text-lg text-[#94A3B8] leading-relaxed mb-6">
                Too many young people lack access to quality tech education and career pathways. Traditional bootcamps are expensive, and schools often can't keep up with industry demands.
              </p>
              <ul className="space-y-4">
                {[
                  'Limited access to coding education in underserved communities',
                  'High cost of traditional tech bootcamps',
                  'Gap between school curriculum and industry needs',
                  'Lack of mentorship and career guidance'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#FF6A00]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-[#FF6A00]"></div>
                    </div>
                    <span className="text-[#94A3B8]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#00AEEF]/20 to-[#00FFE0]/20 rounded-3xl blur-3xl"></div>
              <img
                src="https://images.pexels.com/photos/5621938/pexels-photo-5621938.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Diverse students learning"
                className="relative rounded-3xl border border-[#00AEEF]/20 w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-24 sm:py-32 bg-[#0A1F44]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
              Our Solution
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
              8-Week Transformation Program
            </h2>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              Our intensive program takes students from complete beginners to confident builders with real, deployed projects in just 8 weeks.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {journey.map((item, index) => (
              <div
                key={index}
                className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-8 hover:border-[#00FFE0]/50 transition-all duration-500 group"
              >
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: `${item.color}20` }}
                >
                  <item.icon className="w-8 h-8" style={{ color: item.color }} />
                </div>
                <span 
                  className="text-xs font-bold uppercase tracking-[0.2em] mb-2 block"
                  style={{ color: item.color }}
                >
                  Stage {index + 1}: {item.stage}
                </span>
                <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-[#94A3B8] leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 sm:py-32 bg-[#060F22]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
              Our Impact
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
              Real Results, Real Futures
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-8 text-center hover:border-[#00FFE0]/50 transition-all duration-500"
              >
                <stat.icon className="w-10 h-10 text-[#00FFE0] mx-auto mb-4" />
                <div className="text-5xl font-black text-white mb-2">{stat.value}</div>
                <div className="text-[#94A3B8] font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Day Section */}
      <section className="py-24 sm:py-32 bg-[#0A1F44]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF6A00]/20 to-[#00AEEF]/20 rounded-3xl blur-3xl"></div>
              <img
                src="https://images.pexels.com/photos/1708912/pexels-photo-1708912.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Demo Day Presentation"
                className="relative rounded-3xl border border-[#00AEEF]/20 w-full"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
                Demo Day
              </span>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
                Showcase Your Work to the World
              </h2>
              <p className="text-lg text-[#94A3B8] leading-relaxed mb-8">
                Every cohort culminates in Demo Day—where students present their deployed projects to tech professionals, potential employers, and the community. It's more than a presentation; it's a launchpad for careers.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Present to industry professionals',
                  'Network with potential employers',
                  'Receive portfolio-ready projects',
                  'Build confidence and communication skills'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#00FFE0] flex-shrink-0" />
                    <span className="text-[#94A3B8]">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/programs"
                data-testid="demo-learn-more-btn"
                className="inline-flex items-center justify-center bg-transparent border-2 border-[#00AEEF] text-[#00AEEF] hover:bg-[#00AEEF]/10 hover:text-white font-bold rounded-full px-8 py-3.5 transition-all duration-300 gap-2"
              >
                Learn More About Our Programs
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Student/Parent Portal CTA Section */}
      <section className="py-16 sm:py-20 bg-[#060F22]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="bg-gradient-to-r from-[#0A1F44] to-[#0A1F44]/70 rounded-3xl p-8 sm:p-12 border border-[#00AEEF]/20">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
                  Already Enrolled?
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-4">
                  Access Your Learning Portal
                </h2>
                <p className="text-[#94A3B8] leading-relaxed">
                  Students and parents can log in to access courses, track progress, and view assignments.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Link
                  to="/student/login"
                  data-testid="home-student-portal-btn"
                  className="bg-[#060F22] border-2 border-[#00AEEF] rounded-2xl p-6 hover:border-[#00FFE0] hover:bg-[#00AEEF]/5 transition-all group"
                >
                  <div className="w-14 h-14 rounded-xl bg-[#00AEEF]/20 flex items-center justify-center mb-4 group-hover:bg-[#00AEEF]/30 transition-colors">
                    <GraduationCap className="w-7 h-7 text-[#00AEEF]" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Student Portal</h3>
                  <p className="text-[#94A3B8] text-sm mb-4">Access courses, complete lessons, and submit assignments</p>
                  <span className="inline-flex items-center gap-2 text-[#00AEEF] font-semibold group-hover:text-[#00FFE0]">
                    Login <ArrowUpRight className="w-4 h-4" />
                  </span>
                </Link>
                <Link
                  to="/parent/login"
                  data-testid="home-parent-portal-btn"
                  className="bg-[#060F22] border-2 border-[#00FFE0] rounded-2xl p-6 hover:border-[#00AEEF] hover:bg-[#00FFE0]/5 transition-all group"
                >
                  <div className="w-14 h-14 rounded-xl bg-[#00FFE0]/20 flex items-center justify-center mb-4 group-hover:bg-[#00FFE0]/30 transition-colors">
                    <Users className="w-7 h-7 text-[#00FFE0]" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Parent Portal</h3>
                  <p className="text-[#94A3B8] text-sm mb-4">Track your child's progress and view their achievements</p>
                  <span className="inline-flex items-center gap-2 text-[#00FFE0] font-semibold group-hover:text-[#00AEEF]">
                    Login <ArrowUpRight className="w-4 h-4" />
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-[#060F22] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#00AEEF]/10 via-transparent to-transparent"></div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
            Start Your Journey
          </span>
          <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
            Ready to Build Your Future?
          </h2>
          <p className="text-lg text-[#94A3B8] leading-relaxed mb-8 max-w-2xl mx-auto">
            Join the next cohort and transform from a complete beginner to a confident tech builder. No experience required—just the drive to learn.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/enrollment"
              data-testid="cta-enroll-btn"
              className="inline-flex items-center justify-center bg-[#FF6A00] text-white font-bold rounded-full px-8 py-4 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] hover:-translate-y-1 transition-all duration-300 gap-2 text-lg"
            >
              Apply Now
              <ArrowUpRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="inline-flex items-center justify-center bg-transparent border-2 border-[#00AEEF] text-[#00AEEF] hover:bg-[#00AEEF]/10 hover:text-white font-bold rounded-full px-8 py-4 transition-all duration-300 gap-2 text-lg"
            >
              Contact Us
            </Link>
          </div>

          {/* Newsletter */}
          <div className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-8 max-w-xl mx-auto">
            <h3 className="text-xl font-bold text-white mb-2">Stay Updated</h3>
            <p className="text-[#94A3B8] mb-6">Get notified about enrollment dates, events, and success stories.</p>
            <NewsletterForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
